<script setup lang="ts">
  import { OptionType } from '@/enums/OptionType';
  import { IItem } from '@/interfaces/IItem';
  import { IOption } from '@/interfaces/IOption';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { inject, Ref, computed } from 'vue';
  import ControlNumberInput from '../control/ControlNumberInput.vue';

  const restaurantStore = useRestaurantStore();

  const props = defineProps<{
    value?: number | undefined;
    parent: IOption;
    optionItem: IOptionItem;
    disabled?: boolean;
    leftToSelect?: number;
    onlyDisplay?: boolean;
  }>();

  const emits = defineEmits(['input']);

  const item = inject<Ref<IItem | null>>('item')!;

  const isAdditional = computed(() => {
    return props.parent.type === OptionType.Add;
  });

  const val = computed({
    get: () => props.value ?? 0,
    set: (value) => {
      emits('input', value);
    },
  });

  const checked = computed({
    get: () => !!val.value,
    set: (value) => {
      val.value = value ? 1 : 0;
    },
  });

  const optionPrice = computed(() => {
    const oI = props.optionItem;
    return oI.discountedCost ?? oI.cost;
  });

  const shouldDisplayPrice = computed(() => {
    if (!props.parent.main) {
      return optionPrice.value > 0;
    } else {
      if (restaurantStore.restaurantSettings?.variantsFullPrice) {
        return true;
      } else {
        const itemFinalPrice = item.value!.discountedPrice ?? item.value!.price;
        return optionPrice.value - itemFinalPrice !== 0;
      }
    }
  });

  const formattedPrice = computed(() => {
    const symbol = restaurantStore.currency?.symbol;
    if (!props.parent.main) {
      return `+ ${symbol}${displayPrice(optionPrice.value)}`;
    } else if (restaurantStore.restaurantSettings?.variantsFullPrice) {
      return `${symbol}${displayPrice(optionPrice.value)}`;
    } else {
      const itemFinalPrice = item.value!.discountedPrice ?? item.value!.price;
      const priceDiff = optionPrice.value - itemFinalPrice;
      const sign = priceDiff >= 0 ? '+' : '-';
      return `${sign} ${symbol}${displayPrice(Math.abs(priceDiff))}`;
    }
  });

  const max = computed(() => {
    if (!props.parent.multiple) return 1;

    if (typeof props.leftToSelect === 'undefined') {
      return props.parent.max;
    } else {
      return Math.min(
        props.parent.max,
        (props.value ?? 0) + props.leftToSelect
      );
    }
  });

  const isActive = computed(() => {
    return props.optionItem.active;
  });

  const pictureUrl = computed(() => {
    if (!props.optionItem.picture) return null;
    return `${Globals.uploadsPath}/${props.optionItem.picture}`;
  });
</script>

<template>
  <div
    class="option-item"
    :class="{
      'is-additional': isAdditional,
      'is-option': !isAdditional,
      disabled: disabled,
      'only-display': onlyDisplay,
      inactive: !isActive,
    }"
    @click="
      !onlyDisplay &&
        !isAdditional &&
        !disabled &&
        isActive &&
        (checked = !checked)
    "
  >
    <img v-if="pictureUrl" class="option-picture" :src="pictureUrl" alt="" />
    <div class="details">
      <span class="item-title">{{ optionItem.title }}</span>
      <span v-if="optionItem.description" class="item-description">{{
        optionItem.description
      }}</span>
      <template v-if="shouldDisplayPrice && isActive">
        <div class="price-container">
          <span class="price">{{ formattedPrice }}</span>
        </div>
      </template>
      <template v-else-if="!isActive">
        <div class="price-container">
          <span class="price">No disponible</span>
        </div>
      </template>
    </div>
    <div v-if="!onlyDisplay" class="actions">
      <template v-if="!isAdditional">
        <v-checkbox
          v-if="parent.multiple"
          v-model="checked"
          :disabled="disabled || !isActive"
          hide-details
          dense
          class="ma-0 pa-0 tw-pointer-events-none"
        />
        <v-checkbox
          v-else
          v-model="checked"
          :disabled="disabled || !isActive"
          hide-details
          dense
          class="ma-0 pa-0 tw-pointer-events-none"
        />
      </template>
      <template v-else>
        <v-btn
          v-if="!val"
          text
          x-large
          class="increment-button ma-0"
          :disabled="disabled || !isActive"
          @click="val++"
        >
          +
        </v-btn>
        <ControlNumberInput
          v-else
          v-model="val"
          :min="0"
          :max="max"
          size="md"
          :disabled="disabled || !isActive"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .option-item {
    @apply tw-flex tw-items-center tw-justify-between tw-space-x-4 tw-px-4 tw-py-1 tw-min-h-16;
    @apply hover:tw-bg-gray-50;

    &.is-option {
      @apply tw-cursor-pointer;
    }

    &.disabled {
      @apply tw-cursor-not-allowed;
    }

    &.inactive {
      @apply tw-opacity-50 tw-cursor-not-allowed;
    }

    &.only-display {
      @apply tw-cursor-auto;
    }

    .option-picture {
      @apply tw-h-12 tw-w-12 tw-flex-none tw-rounded-lg tw-object-cover;
    }

    .details {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

      .item-title {
        @apply tw-text-gray-700;
      }

      .item-description {
        @apply tw-text-xs tw-text-gray-500;
      }

      .price-container {
        @apply tw-text-sm tw-text-gray-500 tw-tracking-wide;
      }
    }

    .actions {
      .increment-button {
        font-size: 1.25rem !important;
        padding: 0.5rem 1rem !important;
      }

      .number-input {
        :deep(.v-input__control) {
          .v-input__slot {
            padding: 0 8px !important;
            margin-bottom: 0;
          }
        }
      }
    }
  }
</style>
